interface UploadResponse {
  success: boolean;
  /** R2 object key — this is what should be stored, not the absolute URL. */
  key?: string;
  imageUrl?: string;
  error?: string;
}

/**
 * Convert image to WebP format and upload to Cloudflare R2
 */
export async function uploadProfileImage(file: File): Promise<UploadResponse> {
  try {
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      return {
        success: false,
        error: 'Image must be less than 5MB'
      };
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      return {
        success: false,
        error: 'File must be an image'
      };
    }

    // Convert to WebP
    const webpBlob = await convertToWebP(file);
    
    // Create form data for upload
    const formData = new FormData();
    formData.append('file', webpBlob, 'profile.webp');

    // Upload to R2 via our API
    const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
    const response = await fetch(`${baseURL}/api/upload/profile-image`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        error: error.error || 'Upload failed'
      };
    }

    const result = await response.json();
    return {
      success: true,
      key: result.key,
      imageUrl: result.imageUrl
    };

  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      error: 'Upload failed'
    };
  }
}

/**
 * Convert image file to WebP format
 */
async function convertToWebP(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Set canvas dimensions
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw image to canvas
      ctx?.drawImage(img, 0, 0);

      // Convert to WebP
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert to WebP'));
          }
        },
        'image/webp',
        0.9 // Quality: 90%
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    
    // Load the image
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Update user profile image in database
 */
export async function updateProfileImage(imageUrl: string): Promise<UploadResponse> {
  try {
    const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
    const response = await fetch(`${baseURL}/api/user/profile-image`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ imageUrl }),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        error: error.error || 'Failed to update profile image'
      };
    }

    return {
      success: true
    };

  } catch (error) {
    console.error('Update error:', error);
    return {
      success: false,
      error: 'Failed to update profile image'
    };
  }
}
